import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, lazy, useState, useEffect, useMemo } from "react";
import { WagmiProvider } from 'wagmi';
import { config as defaultConfig, buildWagmiConfig } from './wagmi';
import { MainLayout } from "./components/layout/MainLayout";
import { getWcProjectId } from "./lib/wcConfig";
import { GeoGate } from "./components/markets/GeoGate";

// Lazy Load Pages
const Index = lazy(() => import("./pages/Index"));
const Swap = lazy(() => import("./pages/Swap"));
const Pools = lazy(() => import("./pages/Pools"));
const PoolDetails = lazy(() => import("./pages/PoolDetails"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const History = lazy(() => import("./pages/History"));
const Settings = lazy(() => import("./pages/Settings"));
const Status = lazy(() => import("./pages/Status"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Bet = lazy(() => import("./pages/Bet"));
const MatchDetail = lazy(() => import("./pages/MatchDetail"));
const Docs = lazy(() => import("./pages/Docs"));
const Markets = lazy(() => import("./pages/Markets"));
const MarketDetail = lazy(() => import("./pages/MarketDetail"));
const MarketAdmin = lazy(() => import("./pages/MarketAdmin"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const App = () => {
  const [wcProjectId, setWcProjectId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getWcProjectId().then((id) => {
      setWcProjectId(id);
      setLoaded(true);
    });
  }, []);

  // Rebuild config when WC project ID is fetched (only if different from build-time)
  const wagmiConfig = useMemo(() => {
    if (!loaded) return defaultConfig;
    return wcProjectId ? buildWagmiConfig(wcProjectId) : defaultConfig;
  }, [wcProjectId, loaded]);

  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <MainLayout>
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/swap" element={<Swap />} />
                  <Route path="/pools" element={<Pools />} />
                  <Route path="/pool/:address" element={<PoolDetails />} />
                  <Route path="/portfolio" element={<Portfolio />} />
                  <Route path="/history" element={<History />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/status" element={<Status />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/bet" element={<Bet />} />
                  <Route path="/bet/:matchId" element={<MatchDetail />} />
                  <Route path="/docs" element={<Docs />} />
                  {/* FluxMarkets routes — geo-blocked for restricted jurisdictions */}
                  <Route path="/markets" element={<GeoGate><Markets /></GeoGate>} />
                  <Route path="/markets/admin" element={<MarketAdmin />} />
                  <Route path="/markets/:id" element={<GeoGate><MarketDetail /></GeoGate>} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </MainLayout>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};

export default App;
